/**
 * VulnerabilityDrawerHeader Component
 *
 * Displays the vulnerability header section with a two-row layout:
 * - Row 1: Full-width vulnerability name with share button
 * - Row 2: Badges (left) | Status boxes (right)
 *
 * Pure presentation component - mirrors AssetDrawerHeader layout pattern.
 */

import { ShareIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

import { Button, DashedTag, RiskTag } from '@/components/chariot-ui';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { DateStatusBox } from '@/components/DateStatusBox';
import { OverflowText } from '@/components/OverflowText';
import { ScanStatus } from '@/components/ScanStatusContainer';
import { Tooltip } from '@/components/Tooltip';
import { VulnerabilityStatusComponent } from '@/sections/vulnerabilities/components/VulnerabilityStatus';
import { Risk, RiskStatus } from '@/types';

import { getRiskTagSeverity } from './VulnerabilityDrawer';

// Helper type for ML prediction data from history
export interface MLPrediction {
  recommendation: string;
  logit: number;
  model: string;
  timestamp: string;
}

export interface VulnerabilityDrawerHeaderProps {
  risk: Risk;
  existingWriteup?: { title?: string };
  vulnerabilityTitle?: string;
  mlPrediction?: MLPrediction;
  isSSO: boolean;
  ssoTags: string[];
  isOsint?: boolean;
}

/**
 * MLPredictionBadge renders the AI analysis recommendation badge
 */
function MLPredictionBadge({ prediction }: { prediction: MLPrediction }) {
  const getRecommendationText = (rec: string) => {
    switch (rec) {
      case 'D':
        return 'Recommend to reject';
      case 'O':
        return 'Recommend to open';
      case 'T':
        return 'Need triage';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="flex h-full items-center justify-center gap-2 whitespace-nowrap">
      <span className="text-sm">
        AI Analysis: {getRecommendationText(prediction.recommendation)}
      </span>
      <Tooltip
        title={
          <div className="flex flex-col">
            <p>AI score: {prediction.logit.toFixed(2)}</p>
          </div>
        }
      >
        <InformationCircleIcon className="text-chariot-text-primary size-4" />
      </Tooltip>
    </div>
  );
}

/**
 * VulnerabilityDrawerHeader renders the top section of the vulnerability drawer with:
 * - Row 1: Vulnerability name with copy functionality and share button
 * - Row 2: Left side (severity badge, ML prediction, SSO indicator) | Right side (status boxes)
 *
 * This is a pure presentation component with no internal state.
 */
export function VulnerabilityDrawerHeader({
  risk,
  existingWriteup,
  vulnerabilityTitle,
  mlPrediction,
  isSSO,
  ssoTags,
  isOsint = false,
}: VulnerabilityDrawerHeaderProps) {
  const handleShareClick = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Copied URL to clipboard');
  };

  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="vulnerability-drawer-header"
    >
      {/* Row 1: Full-width vulnerability name with share button */}
      <div className="flex items-center gap-2">
        <CopyToClipboard
          alwaysShowCopyIcon
          className="min-w-0 flex-1 gap-2 overflow-hidden [&_svg]:ml-0"
        >
          <OverflowText
            text={
              vulnerabilityTitle ||
              existingWriteup?.title ||
              risk.title ||
              risk.name
            }
            className="text-chariot-text-primary overflow-hidden text-xl font-bold whitespace-nowrap"
          />
        </CopyToClipboard>
        <Button
          styleType="icon"
          className="shrink-0 p-0"
          startIcon={<ShareIcon className="text-chariot-text-primary size-4" />}
          onClick={handleShareClick}
          aria-label="Share vulnerability URL"
          data-testid="share-vulnerability"
          label=""
        />
      </div>

      {/* GUID display */}
      {risk.guid && (
        <div className="flex items-center gap-1.5">
          <span className="text-chariot-text-secondary text-xs">ID:</span>
          <CopyToClipboard textToCopy={risk.guid} className="gap-1">
            <Tooltip title={risk.guid}>
              <span className="text-chariot-text-secondary font-mono text-xs">
                {risk.guid.substring(
                  0,
                  risk.guid.indexOf('-') > 0 ? risk.guid.indexOf('-') : 8
                )}
              </span>
            </Tooltip>
          </CopyToClipboard>
        </div>
      )}
      {/* Row 2: Badges (left) | Status boxes (right) */}
      <div className="flex items-start justify-between gap-6">
        {/* Left side: severity badge, ML prediction, SSO indicator */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <RiskTag severity={getRiskTagSeverity(risk.status)} size="xs" />
          </div>
          {mlPrediction && risk.status[0] === RiskStatus.Triaged && (
            <div className="flex items-center gap-2">
              <MLPredictionBadge prediction={mlPrediction} />
            </div>
          )}
          {isSSO && (
            <div className="flex items-center gap-2">
              <Tooltip title={ssoTags.join(', ')}>
                <div>
                  <DashedTag label="SSO identified" size="xs" />
                </div>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Right side: status boxes - hide Scan Status in OSINT mode */}
        <div className="flex shrink-0 items-start gap-2">
          {!isOsint && <ScanStatus risk={risk} />}
          {!isOsint && (
            <div className="bg-chariot-background-secondary flex h-[88px] min-w-[153px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-4">
              <p className="text-chariot-text-primary text-xs font-semibold">
                Vulnerability Status
              </p>
              <VulnerabilityStatusComponent vulnerability={risk} />
            </div>
          )}
          <DateStatusBox firstSeen={risk.created} lastSeen={risk.visited} />
        </div>
      </div>
    </div>
  );
}
